import moment from "moment"
import { DATE_FORMAT, ROUTE_PATHS, TABLE_GAME_STATUS_FILTER } from "../utilities/constants.js"
import { getSessionGroupReports, getUserData, sessionTimeSortParms } from "./data.js"
import { scoreFilterParamStringForAllScores, scoreFilterParamStringFromScoreRange } from "./detailsFilters.js"
import { encodeRFC3986URIComponent } from "./uiUtils.js"

export const mergeSessionUserCaches = (userCaches = []) => {
  const unifiedCache = {}
  for (const uc of userCaches) {
    Object.assign(unifiedCache, uc)
  }
  return unifiedCache
}

export const mergeUserCachesForSessionGroups = (sessionGroups = []) => {
  // Accumulate all userCaches
  const allUserCaches = []
  for (const sessionGroup of sessionGroups) {
    if (sessionGroup.sessions) {
      allUserCaches.push(...(sessionGroup.sessions.map((sesh) => sesh.userCache) ?? []))
    }
  }
  return mergeSessionUserCaches(allUserCaches)
}

export const sessionGroupReportUserCachesFromGroup = (sessionGroup) => {
  return sessionGroup?.report?.sessionReports?.map((seshReport) => seshReport.userCache) ?? []
}

export const uniqueParticipantEmailsFromGroup = (sessionGroup) => {
  const allUserCaches = sessionGroupReportUserCachesFromGroup(sessionGroup)
  const groupUserCache = mergeSessionUserCaches(allUserCaches)
  const uniqueUserEmails = new Set()
  for (const uc of Object.values(groupUserCache)) {
    uniqueUserEmails.add(uc.email)
  }
  return [...uniqueUserEmails]
}

export const averageSimulationTimeMinutesFromGroup = (sessionGroup) => {
  return sessionGroup?.report?.averageElapsedTime ?? 0
}

export const groupRecommendationsDataFromGroup = (sessionGroup) => {
  return sessionGroup?.metadata?.groupRecommendations?.groupRecommendationsMapsByTurn
}

export const groupDashboardURL = (groupName) => {
  return groupName === null ? ROUTE_PATHS.GROUP_DASHBOARD : ROUTE_PATHS.GROUP_DASHBOARD + `/${encodeRFC3986URIComponent(groupName)}`
}

export const groupRecommendationsURL = (groupName) => {
  return groupName === null
    ? ROUTE_PATHS.GROUP_DASHBOARD + `/${ROUTE_PATHS.DETAILS.ROOT}/${ROUTE_PATHS.DETAILS.RECOMMENDATIONS}`
    : ROUTE_PATHS.GROUP_DASHBOARD + `/${encodeRFC3986URIComponent(groupName)}/${ROUTE_PATHS.DETAILS.ROOT}/${ROUTE_PATHS.DETAILS.RECOMMENDATIONS}`
}

export const groupActionsListURL = ({ groupName, trendGroupName = null }) => {
  // Build the full url (w/ trendGroupName if needed)
  if (groupName && trendGroupName) {
    return (
      ROUTE_PATHS.GROUP_DASHBOARD +
      `/${encodeRFC3986URIComponent(groupName)}/${ROUTE_PATHS.DETAILS.ROOT}/${
        ROUTE_PATHS.DETAILS.ACTIONS_LIST_WITH_TRENDS
      }/${encodeRFC3986URIComponent(trendGroupName)}`
    )
  } else if (groupName) {
    return ROUTE_PATHS.GROUP_DASHBOARD + `/${encodeRFC3986URIComponent(groupName)}/${ROUTE_PATHS.DETAILS.ROOT}/${ROUTE_PATHS.DETAILS.ACTIONS_LIST}`
  }

  return ROUTE_PATHS.GROUP_DASHBOARD + `/${ROUTE_PATHS.DETAILS.ROOT}/${ROUTE_PATHS.DETAILS.ACTIONS_LIST}`
}

export const groupIndividualPerformanceListURL = ({ groupName, trendGroupName = null, scoreRange = null }) => {
  // Compute score range params
  let paramString = ""
  if (scoreRange) {
    paramString = scoreFilterParamStringFromScoreRange(scoreRange)
  } else {
    paramString = scoreFilterParamStringForAllScores()
  }

  // Build the full url (w/ trendGroupName if needed)
  if (groupName && trendGroupName) {
    return (
      ROUTE_PATHS.GROUP_DASHBOARD +
      `/${encodeRFC3986URIComponent(groupName)}/${ROUTE_PATHS.DETAILS.ROOT}/${
        ROUTE_PATHS.DETAILS.INDIVIDUAL_PERFORMANCE_WITH_TRENDS
      }/${encodeRFC3986URIComponent(trendGroupName)}${paramString ? `?${paramString}` : ""}`
    )
  } else if (groupName) {
    return (
      ROUTE_PATHS.GROUP_DASHBOARD +
      `/${encodeRFC3986URIComponent(groupName)}/${ROUTE_PATHS.DETAILS.ROOT}/${ROUTE_PATHS.DETAILS.INDIVIDUAL_PERFORMANCE}${
        paramString ? `?${paramString}` : ""
      }`
    )
  }

  return null
}

export const groupActionsDetailURL = ({ groupName, actionItemId }) => {
  if (groupName && actionItemId) {
    return (
      ROUTE_PATHS.GROUP_DASHBOARD +
      `/${encodeRFC3986URIComponent(groupName)}/${ROUTE_PATHS.DETAILS.ROOT}/${
        ROUTE_PATHS.DETAILS.RECOMMENDATION_ACTION_ROOT
      }/${encodeRFC3986URIComponent(actionItemId)}`
    )
  }

  return null
}

export const scenarioNameForSessionGroup = (sessionGroup) => {
  return sessionGroup?.report?.sessionReports?.[0]?.reportData?.reportItems?.scenario
}

export const groupSupportsRunbookGeneration = (sessionGroup) => {
  let supportsRunbookGeneration = false
  const sessionReports = sessionGroup?.report?.sessionReports ?? []
  // All member sessions must support runbook generation
  if (sessionReports.length > 0 && sessionReports.every((aReport) => aReport.canGenerateRunbook)) {
    supportsRunbookGeneration = true
  }
  return supportsRunbookGeneration
}

export const groupCustomReportVariableIds = (sessionGroup) => {
  // Check custom report variable Ids on first member game
  const customReportVarIds = sessionGroup?.report?.sessionReports?.[0]?.customReportVariableIds ?? []
  return customReportVarIds
}

export const groupDashboardTitle = ({ scenarioName } = {}) => {
  const timestamp = moment().format(`${DATE_FORMAT}`)
  const scenarioTitle = scenarioName || "Incident Summary"
  return `${scenarioTitle} - ${timestamp}`
}

export const rolesInfoForSessionGroup = (sessionGroup) => {
  const sessionRoleTable = {}
  if (sessionGroup?.report?.sessionReports) {
    // Iterate over each game session and  deposit role info for each player (by email)
    sessionGroup.report.sessionReports.map((seshReport) => {
      const sessionName = seshReport.session
      const sessionRoleAssignments = seshReport.roleAssignments
      if (sessionName && sessionRoleAssignments) {
        sessionRoleTable[sessionName] = sessionRoleAssignments
      }
    })
  }
  return sessionRoleTable
}

export const userCacheEntryForEmail = (email, userCache) => {
  if (userCache) {
    for (const entry of Object.values(userCache)) {
      if (entry?.email === email) {
        return entry
      }
    }
  }
  // The cache is populated during gameplay, so a scheduled group (and single-role
  // games, which pre-assign the one player to every role) can reference emails
  // that have no entry yet. Return a usable fallback instead of undefined — many
  // callers read .name/.picture directly and would otherwise throw.
  return { email, name: email, picture: null }
}

export const userCacheEmailForId = ({ userId, userCache }) => {
  return userCache?.[userId]?.email ?? null
}

// True when a completed-action `user`/user_id belongs to an AI bot (not a human). Bot ids are
// prefixed bot:/persona:/custom: (mirrors server chaosPlayerUtils.isBotUser). Used to exclude bot
// actions from the score and to tag bot rows in the report.
export const isBotUser = (userId) =>
  typeof userId === "string" && (userId.startsWith("bot:") || userId.startsWith("persona:") || userId.startsWith("custom:"))

// Resolve a bot action's display name from the composed session's botParticipants. The action
// user id is `bot:<key>`; botParticipants rows are keyed by `<key>` (botKey). Returns null for
// non-bot ids or when no matching participant is found.
export const botNameForUserId = ({ userId, botParticipants }) => {
  if (!isBotUser(userId) || !Array.isArray(botParticipants)) return null
  const key = userId.startsWith("bot:") ? userId.slice(4) : userId
  const bp = botParticipants.find((b) => b?.botKey === key || b?.botKey === userId)
  return bp?.name || bp?.role || null
}

export const fetchSessionGroupUserData = async ({ userId, accessToken, sessionGroup }) => {
  // Collect all unique player emails
  const allPlayerEmailsSet = new Set()
  sessionGroup?.report?.sessionReports?.forEach((sessionReport) => {
    Object.values(sessionReport.roleAssignments).forEach((roleEmail) => {
      allPlayerEmailsSet.add(roleEmail)
    })
  })
  const userData = await getUserData(accessToken, userId, {
    emails: [...allPlayerEmailsSet],
  })
  return userData
}

export const dispatchTimeForSessionGroup = (sessionGroup) => {
  // Compute earliest (non-null) dispatch time or scheduled time
  const allDispatches = sessionGroup?.report?.sessionReports
    ?.map((seshReport) => seshReport.dispatchTime ?? seshReport.scheduledTime)
    ?.filter((time) => time !== null)

  if (allDispatches && allDispatches.length > 0) {
    const earliestSessionDispatch = Math.min(...allDispatches)
    return new Date(earliestSessionDispatch).toISOString()
  }
  return null
}

const MAX_RELATED_HISTORICAL_SESSIONS = 5

export const fetchRelatedHistoricalSessionGroupsForGroup = async ({ userId, accessToken, sessionGroup }) => {
  const relatedGroups = []
  const test = sessionGroup?.report?.sessionReports?.[0]?.reportData?.reportItems?.scenarioId
  if (test && sessionGroup?.org) {
    let searchResults =
      (await getSessionGroupReports(accessToken, userId, {
        similarToTest: test, // Match test ID or SKU, etc. (fuzzy match)
        org: sessionGroup.org,
        archived: "HIDE",
        // statusFilter: getSessionsStatusFilterParamFor(TABLE_GAME_STATUS_FILTER.FINISHED),
        timeSort: sessionTimeSortParms.ASC,
      })) ?? []

    // Trim groups which aren't completed
    searchResults = searchResults.filter((aGroup) => aGroup.status.toLowerCase() === TABLE_GAME_STATUS_FILTER.FINISHED.toLowerCase())

    // Trim related groups down to a 'window' of 5 games surrounding the 'sessionGroup' itself, preferring the most recent related games
    const thisGroupDispatchTime = dispatchTimeForSessionGroup(sessionGroup)
    let previousGameCount = 0
    let concurrentGameCount = 0 // The selected game and any other 'concurrent' game (unlikely to be >1)
    let followingGameCount = 0

    for (const relatedGroup of searchResults) {
      const relatedDispatchTime = dispatchTimeForSessionGroup(relatedGroup)
      if (relatedDispatchTime < thisGroupDispatchTime) {
        previousGameCount += 1
      } else if (relatedDispatchTime > thisGroupDispatchTime) {
        followingGameCount += 1
      } else {
        concurrentGameCount += 1
      }
    }

    let trimmedResults

    switch (true) {
      case previousGameCount + followingGameCount + concurrentGameCount <= MAX_RELATED_HISTORICAL_SESSIONS:
        trimmedResults = searchResults // show all results
        break
      case followingGameCount + concurrentGameCount <= MAX_RELATED_HISTORICAL_SESSIONS: {
        const startIndex = previousGameCount + followingGameCount + concurrentGameCount - MAX_RELATED_HISTORICAL_SESSIONS
        trimmedResults = searchResults.slice(startIndex) // trim older games from results
        break
      }
      case concurrentGameCount <= MAX_RELATED_HISTORICAL_SESSIONS: {
        const startIndex = previousGameCount
        const endIndex = previousGameCount + MAX_RELATED_HISTORICAL_SESSIONS
        trimmedResults = searchResults.slice(startIndex, endIndex) // trim older games first, then trim (minimal) newer games
        break
      }
      default: {
        const startIndex = previousGameCount
        const endIndex = previousGameCount + concurrentGameCount
        trimmedResults = searchResults.slice(startIndex, endIndex) // only return concurrent games
        break
      }
    }

    relatedGroups.push(...trimmedResults)
  }
  return relatedGroups
}

export const fetchPeerScoreSessionGroupsForGroup = async ({ userId, accessToken, sessionGroup }) => {
  const relatedGroups = []
  const test = sessionGroup?.report?.sessionReports?.[0]?.reportData?.reportItems?.scenarioId
  if (test && sessionGroup?.org) {
    let searchResults =
      (await getSessionGroupReports(accessToken, userId, {
        similarToTest: test, // Match test ID or SKU, etc. (fuzzy match)
        org: sessionGroup.org,
        archived: "HIDE",
        // statusFilter: getSessionsStatusFilterParamFor(TABLE_GAME_STATUS_FILTER.FINISHED),
        timeSort: sessionTimeSortParms.ASC,
      })) ?? []

    // Trim groups which aren't completed
    searchResults = searchResults.filter((aGroup) => aGroup.status.toLowerCase() === TABLE_GAME_STATUS_FILTER.FINISHED.toLowerCase())

    relatedGroups.push(...searchResults)
  }
  return relatedGroups
}
